// dodatkowe filtry pod listą kategorii

import React, { FC, useEffect, useState } from 'react';
import { ExclamationCircle, ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import classnames from 'classnames';

import { IProductsFilter, IProductsFilterValue } from 'api/types';
import { Button } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/wise/WiseProducts/components/WiseFiltersSidebar/WiseFiltersSidebar.module.scss';

interface IFilter {
  filter_id: string;
  filter_value: string;
}

// typ danych wejściowych
interface IProps {
  categoryId?: number;
  queryFilters: IFilter[];
  filtersData?: IProductsFilter[];
  onChange: (filters: IFilter[]) => void;
}

const WiseFiltersSidebar: FC<IProps> = ({ categoryId, onChange, queryFilters, filtersData }) => {
  const { t } = useTranslation();

  // lista zqaznaczonych filtrów
  const [currentProductsFilters, setCurrentProductsFilters] = useState<
    { filter_id: string; filter_value: string }[]
  >([]);

  // stan zwijania/rozwijania filtrów
  const [isExpanded, setIsExpanded] = useState(false);

  // nadpisanie lokalnych filtrów przy zmienie filtrów z props'ów
  useEffect(() => {
    setCurrentProductsFilters(queryFilters);
  }, [queryFilters]);

  // liczba filtrów do wyświetlenia
  const MAX_VISIBLE_FILTERS = 10;
  const visibleFilters = filtersData?.slice(0, MAX_VISIBLE_FILTERS) || [];
  const hiddenFilters = filtersData?.slice(MAX_VISIBLE_FILTERS) || [];
  const hasMoreFilters = (filtersData?.length || 0) > MAX_VISIBLE_FILTERS;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-wise-WiseProducts-components-WiseFiltersSidebar'
      )}>
      {(!categoryId || (filtersData?.length || 0) > 0) && (
        <div className={styles.title}>{t('Filtry')}</div>
      )}

      {!categoryId && (
        <div className={styles.info}>
          <ExclamationCircle />
          <span>
            <ReactMarkdown disallowedElements={['p']} unwrapDisallowed>
              {t('PRODUCTS_SIDEBAR_NO_FILTERS_INFO')}
            </ReactMarkdown>
          </span>
        </div>
      )}

      {!!categoryId && (filtersData?.length || 0) > 0 && (
        <div className={styles.filtersWrapper}>
          {visibleFilters.map((filter) => (
            <div className={styles.filter} key={filter.id}>
              {filter.type === 'singlechoice' ? (
                <WiseSelect<IProductsFilterValue>
                  value={
                    currentProductsFilters.find((item) => item.filter_id === filter.id)
                      ?.filter_value
                  }
                  onChange={(item) => {
                    setCurrentProductsFilters((prevState) => {
                      const newState = [
                        ...prevState.filter(
                          (assignedFilter) => assignedFilter.filter_id !== filter.id
                        ),
                        ...(item ? [{ filter_id: filter.id, filter_value: item.value }] : [])
                      ];
                      if (item === null) {
                        onChange(newState);
                      }

                      return newState;
                    });
                  }}
                  options={filter.values.map((item) => ({
                    value: item.value,
                    label: item.name,
                    item
                  }))}
                  placeholder={filter.label}
                  clearable
                />
              ) : (
                <div className={styles.unknownType}>
                  {t('Nieznany typ filtra')} (<strong>{filter.type}</strong>)
                </div>
              )}
            </div>
          ))}

          {hasMoreFilters && (
            <div
              className={classnames(styles.hiddenFilters, {
                [styles.expanded]: isExpanded
              })}>
              {hiddenFilters.map((filter) => (
                <div className={styles.filter} key={filter.id}>
                  {filter.type === 'singlechoice' ? (
                    <WiseSelect<IProductsFilterValue>
                      value={
                        currentProductsFilters.find((item) => item.filter_id === filter.id)
                          ?.filter_value
                      }
                      onChange={(item) => {
                        setCurrentProductsFilters((prevState) => {
                          const newState = [
                            ...prevState.filter(
                              (assignedFilter) => assignedFilter.filter_id !== filter.id
                            ),
                            ...(item ? [{ filter_id: filter.id, filter_value: item.value }] : [])
                          ];
                          if (item === null) {
                            onChange(newState);
                          }

                          return newState;
                        });
                      }}
                      options={filter.values.map((item) => ({
                        value: item.value,
                        label: item.name,
                        item
                      }))}
                      placeholder={filter.label}
                      clearable
                    />
                  ) : (
                    <div className={styles.unknownType}>
                      {t('Nieznany typ filtra')} (<strong>{filter.type}</strong>)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasMoreFilters && (
            <div className={styles.expandButton} onClick={() => setIsExpanded(!isExpanded)}>
              <span>
                {isExpanded
                  ? t('Pokaż mniej')
                  : `${t('Pokaż więcej')} (${(filtersData?.length ?? 0) - MAX_VISIBLE_FILTERS})`}
              </span>
              {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </div>
          )}

          <div className={styles.actions}>
            <Button color="secondary" onClick={() => onChange(currentProductsFilters)}>
              {t('Filtruj')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WiseFiltersSidebar;
